import { ACTIONS } from '../constants';
import { STATUS } from '../constants/status';

type Action = { type: string, payload?: Object };

export const initialState = {
  orderId: '',
  isLoading: false,
  isGeneralError: false,
  isSuccess: false,
  isBopisCancelModalOpen: false,
  cancelItemIdList: [],
  selectedItemIdList: [],
  hasSTHItem: false,
};

/* eslint-disable no-param-reassign */
// HOTFIX: if an order has an sth item, we will cancel the full order to prevent fraudulent cancels for free shipping charges
export const itemsInit = (items: array, hasSTHItem: Boolean) =>
  items.reduce((obj: Object, itemId: string) => {
    obj[itemId] = { lineId: itemId, isSelected: hasSTHItem, isError: false };
    return obj;
  }, {});

export const setItemErrors = (state: Object, errorIds: array) =>
  errorIds.reduce((obj: Object, id: array) => {
    obj[id[0]] = { ...state[id[0]], isError: true }; // TODO: double check if id is index or lineId from error obj
    return obj;
  }, {});
/* eslint-enable no-param-reassign */

export const onlineCancelInit = (state: Object, action: Object) => {
  const { orderId, itemIds, hasSTHItem } = action.payload;
  return {
    ...state,
    orderId,
    cancelItemIdList: itemIds,
    hasSTHItem,
    selectedItemIdList: hasSTHItem ? itemIds : [],
    ...itemsInit(itemIds, hasSTHItem),
  };
};

export const filterSelectedItems = (state: Object) =>
  state.cancelItemIdList
    .map((id: string) => state[id])
    .filter((item: Object) => item.isSelected)
    .map((item: Object) => item.lineId);

export const cancelItemsSelect = (state: Object, action: Object) => {
  const { payload } = action;
  const updateItem = {
    ...state,
    [payload]: { ...state[payload], isSelected: !state[payload].isSelected },
  };

  return {
    ...updateItem,
    selectedItemIdList: filterSelectedItems(updateItem),
  };
};

export const cancelItemsRequest = (state: Object) => ({
  ...state,
  isLoading: true,
  isSuccess: false,
  isError: false,
  ...itemsInit(state.cancelItemIdList, state.hasSTHItem),
});

export const cancelItemsSuccess = (state: Object) => ({
  ...state,
  isLoading: false,
  isSuccess: true,
});

export const cancelItemsFailure = (state: Object, action: Object) => {
  const { payload } = action;
  const errors = payload.length ? setItemErrors(state, payload) : {};
  return {
    ...state,
    isLoading: false,
    isGeneralError: !payload.length,
    selectedItemIdList: [],
    ...errors,
  };
};

/* eslint-disable no-param-reassign */
export const convertOrderToCanceled = (state: Object, action: Object) => {
  const { orderId } = action.payload;
  const { orderGroups } = state[orderId];
  const lineAssociation = orderGroups.reduce(
    (cancelLineAssocation: Array, bopisGroup: Object) => [
      ...cancelLineAssocation,
      ...bopisGroup.lineAssociation,
    ],
    []
  );
  const newState = {
    ...state,
    [orderId]: {
      ...state[orderId],
      grandTotal: 0,
      subTotal: 0,
      taxTotal: 0,
      orderGroups: [
        {
          status: 'Canceled',
          statusCode: STATUS.CANCELED,
          lineAssociation,
        },
      ],
    },
  };
  return newState;
};

export const convertItemsToCanceled = (state: Object, action: Object) => {
  // TODO: simplify
  const { orderId } = action.payload;
  const canceledItemKeys = Object.keys(state[orderId] || {});
  const newState = canceledItemKeys.reduce(
    (mappedData: Object, itemId: String) => {
      mappedData[orderId][itemId] = Object.assign(state[orderId][itemId], {
        cancelledQuantity: state[orderId][itemId].currentQuantity,
        currentQuantity: 0,
        unitPrice: 0,
        totalPrice: 0,
      });
      return mappedData;
    },
    state
  );

  const test = { ...state, ...newState };
  return test;
};
/* eslint-enable  no-param-reassign */

export const showLoader = (state: Object) => ({
  ...state,
  isLoading: true,
});

export const hideLoader = (state: Object) => ({
  ...state,
  isLoading: false,
});

export const openBopisModal = (state: Object) => ({
  ...state,
  isBopisCancelModalOpen: true,
});

export const closeBopisModal = (state: Object) => ({
  ...state,
  isBopisCancelModalOpen: false,
  isGeneralError: false,
});

const onlineCancel = (state: Object = initialState, action: Action) => {
  const newState = Object.assign({}, state);
  switch (action.type) {
    case ACTIONS.ONLINE_CANCEL_INIT:
      return onlineCancelInit(newState, action);
    case ACTIONS.ONLINE_CANCEL_RESET:
      return initialState;
    case ACTIONS.CANCEL_ITEMS_SELECT:
      return cancelItemsSelect(newState, action);
    case ACTIONS.CANCEL_ITEMS_REQUEST:
      return cancelItemsRequest(newState);
    case ACTIONS.CANCEL_ITEMS_SUCCESS:
      return cancelItemsSuccess(newState);
    case ACTIONS.CANCEL_ITEMS_FAILURE:
      return cancelItemsFailure(newState, action);
    case ACTIONS.FETCH_ORDER_DETAILS:
      return showLoader(newState);
    case ACTIONS.FETCH_ORDER_DETAILS_SUCCESS:
      return hideLoader(newState);
    case ACTIONS.OPEN_BOPIS_CANCEL_MODAL:
      return openBopisModal(newState);
    case ACTIONS.CLOSE_BOPIS_CANCEL_MODAL:
      return closeBopisModal(newState);
    default:
      return state;
  }
};

export default onlineCancel;

export const getLoading = (state: Object) => state.onlineCancel.isLoading;
export const getGeneralError = (state: Object) =>
  state.onlineCancel.isGeneralError;
export const getSuccess = (state: Object) => state.onlineCancel.isSuccess;
export const getCancelItem = (state: Object, id: string) =>
  state.onlineCancel[id];
export const getCancelItemIdList = (state: Object) =>
  state.onlineCancel.cancelItemIdList;
export const getSelectedItemIdList = (state: Object) =>
  state.onlineCancel.selectedItemIdList;
export const getBopisCancelModalOpen = (state: Object) =>
  state.onlineCancel.isBopisCancelModalOpen;
export const getHasSTHItem = (state: Object) => state.onlineCancel.hasSTHItem;
export const getOrderId = (state: Object) => state.onlineCancel.orderId;
